import * as Sentry from "@sentry/react";
import { axeIntegration } from "@glitchreplay/a11y";
import { networkProbeIntegration } from "@glitchreplay/network-probe";
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";

const PROJECT_ID = "prj_b02be1f1748fc86bcf9c";
const PUBLIC_KEY = "c2abdb60587648734c1f3968c2d9ed79";
const DSN = `https://${PUBLIC_KEY}@glitchreplay.com/0`;

const NOISE_MESSAGES = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
  // Microsoft Office/Outlook web-addin postMessage noise that fires in pages
  // unrelated to Office. Always presents with no stack and no breadcrumbs.
  "Object Not Found Matching Id",
];

Sentry.init({
  dsn: DSN,
  release: import.meta.env.VITE_RELEASE,
  tracesSampleRate: 0,
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
  denyUrls: [
    /unpkg\.com\/@excalidraw/i,
    /cdn\.jsdelivr\.net\/npm\/@excalidraw/i,
    /excalidraw-assets\/vendor-/i,
  ],
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: true,
      blockAllMedia: true,
    }),
    axeIntegration({
      runOn: ["development", "staging", "production"],
      productionSampleRate: 0.05,
      wcagLevel: "AA",
      minImpact: "moderate",
    }),
    networkProbeIntegration({
      verifyResourceErrors: [/imagedelivery\.net/i],
    }),
  ],
  beforeSend(event, hint) {
    const message = event.message || (hint?.originalException as Error)?.message || event.exception?.values?.[0]?.value || "";
    if (NOISE_MESSAGES.some((n) => message.includes(n))) return null;
    const error = hint?.originalException;
    if (message === "Failed to fetch" && !(error instanceof Error && error.stack)) return null;
    const haystack = `${message} ${(error instanceof Error && error.stack) || ""}`;
    if (/unpkg\.com\/@excalidraw|excalidraw-assets\/vendor-/.test(haystack)) return null;
    return event;
  },
});

fetch(`https://glitchreplay.com/api/${PROJECT_ID}/config?sentry_key=${PUBLIC_KEY}`, {
  cache: "no-store",
})
  .then((r) => (r.ok ? r.json() : null))
  .then((cfg) => {
    const replay = Sentry.getReplay();
    if (!replay) return;
    if (!cfg?.replay?.enabled) {
      replay.stop();
      return;
    }
    const sessionRate = Number(cfg.replay.sessionSampleRate ?? 1);
    if (sessionRate < 1 && Math.random() >= sessionRate) {
      replay.stop();
    }
  })
  .catch(() => {});

const Fallback = () => (
  <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0f172a", color: "#e2e8f0", fontFamily: "system-ui, sans-serif", padding: "2rem", textAlign: "center" }}>
    <div>
      <h1 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>Something went wrong</h1>
      <p style={{ opacity: 0.7, marginBottom: "1.5rem" }}>The error has been reported. Please reload the page.</p>
      <button onClick={() => window.location.reload()} style={{ padding: "0.5rem 1rem", borderRadius: "0.5rem", background: "#6366f1", color: "white", border: "none", cursor: "pointer" }}>Reload</button>
    </div>
  </div>
);

const root = document.getElementById("root")!;

if (root.innerHTML.trim() && root.innerHTML !== "<!--app-html-->") {
  ReactDOM.hydrateRoot(
    root,
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={Fallback}>
        <App />
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );
} else {
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={Fallback}>
        <App />
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );
}
